import { useState } from "react";
import { Share2, MessageCircle, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const SITE_URL =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://tigersforkids.com";

function buildMessage({ playerName, quizScore, gameScore }) {
  const lines = [
    `🐯 I'm learning all about TIGERS on Tigersforkids.com!`,
  ];
  if (playerName) {
    if (quizScore != null) {
      lines.push(
        `${playerName} scored ${quizScore.score}/${quizScore.total} on the Tiger Quiz! 🌟`,
      );
    }
    if (gameScore != null) {
      lines.push(
        `${playerName} got ${gameScore.score} points in the Tiger Adventure game!`,
      );
    }
  }
  lines.push(`Can you beat my score? Play here:`);
  lines.push(SITE_URL);
  return lines.join("\n");
}

export default function ShareCard({
  title = "Challenge a friend!",
  subtitle = "Share Tigersforkids with your buddies and see who knows more.",
  playerName,
  quizScore,
  gameScore,
  variant = "light",
}) {
  const [copied, setCopied] = useState(false);
  const message = buildMessage({ playerName, quizScore, gameScore });
  const encoded = encodeURIComponent(message);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Link copied! 🔗");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy. Try the WhatsApp button instead!");
    }
  };

  const containerCls =
    variant === "dark"
      ? "bg-tiger-deep text-tiger-sand"
      : "bg-tiger-cream text-tiger-deep";
  const titleCls = variant === "dark" ? "text-sun" : "text-tiger-deep";
  const subCls = variant === "dark" ? "text-tiger-sand/80" : "text-tiger-deep/70";

  return (
    <section
      className={`rounded-3xl border-[4px] border-tiger-deep p-5 sm:p-6 shadow-stickerLg ${containerCls}`}
      data-testid="share-card"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-2xl border-[3px] border-tiger-deep bg-tiger-orange text-white shadow-sticker">
          <Share2 className="h-6 w-6" />
        </span>
        <div>
          <h3 className={`font-heading text-2xl font-bold ${titleCls}`}>
            {title}
          </h3>
          <p className={`font-body text-sm ${subCls}`}>{subtitle}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <a
          href={`https://wa.me/?text=${encoded}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-jungle-leaf px-5 py-2.5 font-heading text-base font-bold text-white shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent(
            "Check out Tigersforkids.com! 🐯",
          )}&body=${encoded}`}
          className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-sun px-5 py-2.5 font-heading text-base font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
          data-testid="share-email"
        >
          <Mail className="h-4 w-4" /> Email
        </a>
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-5 py-2.5 font-heading text-base font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
          data-testid="share-copy"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" /> Copied!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> Copy link
            </>
          )}
        </button>
      </div>

      <pre
        className={`mt-4 max-h-32 overflow-auto rounded-2xl border-[3px] border-tiger-deep/30 bg-white/60 px-3 py-2 font-body text-xs whitespace-pre-wrap break-words ${
          variant === "dark" ? "text-tiger-deep" : "text-tiger-deep/80"
        }`}
        data-testid="share-message-preview"
      >
        {message}
      </pre>
    </section>
  );
}
